/**
 * Hook: hijack Cmd+F / Ctrl+F to focus an in-app search input.
 *
 * Why: the dashboards render hundreds of rows inside an `overflow-y-auto`
 * scrollable container. Browser-native Ctrl+F searches DOM text — which
 * works in principle but routinely misses for users because (a) they
 * search a string that isn't on the currently scrolled-into-viewport
 * page, (b) the dataset is server-paginated/filtered and the row they
 * want is on another filter state, or (c) zoom/scrollbar interactions
 * leave focus on a hidden element. Our in-app search is server-backed
 * and searches container/job/BL/booking/customer/driver/MBL across the
 * entire dataset, so hijacking the shortcut routes the user to the tool
 * that actually answers their question.
 *
 * Press Esc while focused to drop focus and restore default browser
 * Ctrl+F (the second press triggers native find).
 */
import { useEffect } from 'react';

export const useInAppSearchShortcut = (selector = '[data-testid="search-input"]') => {
  useEffect(() => {
    const handler = (e) => {
      // Cmd+F (Mac) or Ctrl+F (Windows/Linux). Ignore if user already
      // typing in an input/textarea so we don't break form fields.
      const isFindShortcut =
        (e.key === 'f' || e.key === 'F') && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey;
      if (!isFindShortcut) return;
      const target = document.querySelector(selector);
      if (!target) return; // No in-app search available — let browser handle.
      e.preventDefault();
      target.focus();
      // Select any existing value so a fresh type replaces it.
      if (typeof target.select === 'function') {
        try { target.select(); } catch { /* no-op */ }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selector]);
};

export default useInAppSearchShortcut;
